import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 700 }]
});

const C = {
  primary: '#1565C0',
  primaryDark: '#0D47A1',
  primaryLight: '#E3F2FD',
  subtotalBg: '#BBDEFB',
  altRow: '#F5F8FF',
  white: '#FFFFFF',
  dark: '#1A1A2E',
  border: '#B0BEC5',
  red: '#B71C1C',
  gray: '#546E7A'
};

const COL = {
  sr: '4%',
  type: '7%',
  billNo: '12%',
  date: '9%',
  patient: '19%',
  uhid: '11%',
  sponsor: '14%',
  disc: '12%',
  net: '12%'
};

const styles = StyleSheet.create({
  page: { padding: '18pt 20pt', fontSize: 7.5, color: C.dark, backgroundColor: C.white },
  logo: { width: '100%', height: 64, marginBottom: 2 },
  headerRule: { height: 2, backgroundColor: C.primary, marginBottom: 4 },
  reportTitle: { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primaryDark, marginBottom: 1 },
  doctorTitle: { fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primary, marginBottom: 1 },
  reportSub: { fontSize: 7, textAlign: 'center', color: C.gray, marginBottom: 6 },
  tableHead: { flexDirection: 'row', backgroundColor: C.primary, borderRadius: 2 },
  th: {
    color: C.white, fontFamily: 'Roboto', fontWeight: 700, fontSize: 7,
    paddingVertical: 4, paddingHorizontal: 3,
    textAlign: 'center', borderRightWidth: 0.5, borderRightColor: '#90CAF9'
  },
  row: { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: C.border },
  rowEven: { backgroundColor: C.white },
  rowOdd: { backgroundColor: C.altRow },
  td: { fontSize: 7, paddingVertical: 3, paddingHorizontal: 3, borderRightWidth: 0.3, borderRightColor: C.border },
  totalRow: {
    flexDirection: 'row',
    backgroundColor: C.subtotalBg,
    borderTopWidth: 1, borderTopColor: C.primary,
    paddingVertical: 4, paddingHorizontal: 6, marginTop: 2
  },
  totalLabel: { flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', paddingRight: 8 },
  totalAmt: { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right' },
  footer: {
    position: 'absolute', bottom: 10, left: 20, right: 20,
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: C.border, paddingTop: 4
  },
  footerLeft: { flex: 1, fontSize: 6, color: C.gray },
  footerRight: { fontSize: 6, color: C.gray, textAlign: 'right' },
  emptyText: { fontSize: 8, color: C.gray, fontStyle: 'italic', padding: 10, textAlign: 'center' }
});

function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return '-';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}
function fmt(n) {
  return (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function nowStr() {
  const d = new Date();
  return `${fmtDate(d)}  ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const TH = ({ w, children, align = 'center' }) => (
  <PDFText style={[styles.th, { width: w, textAlign: align }]}>{children}</PDFText>
);
const TD = ({ w, children, align = 'center', bold, color }) => (
  <PDFText
    style={[styles.td, { width: w, textAlign: align, color: color || C.dark }, bold ? { fontFamily: 'Roboto', fontWeight: 700 } : {}]}
  >
    {children ?? ''}
  </PDFText>
);

export const ViewDoctorVisitsAndBillsPdf = ({ data }) => {
  if (!data) return null;

  const rows = data?.rows || [];
  const doctorName = data?.doctorName || 'Doctor';
  const printTime = nowStr();

  const totalDisc = rows.reduce((sum, r) => sum + (parseFloat(r?.disc_amt) || 0), 0);
  const totalNet = rows.reduce((sum, r) => sum + (parseFloat(r?.net_amt) || 0), 0);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, backgroundColor: 'white' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Doctor Visits and Bills Report">
          <PDFPage style={styles.page} size="A4" orientation="landscape" wrap>
            <PDFImage src="/logo_Hospital_m.jpeg" style={styles.logo} />
            <PDFView style={styles.headerRule} />
            <PDFText style={styles.reportTitle}>VIEW DOCTOR VISITS AND BILLS</PDFText>
            <PDFText style={styles.doctorTitle}>{doctorName}</PDFText>
            <PDFText style={styles.reportSub}>
              {data?.from_date || data?.to_date ? `Period: ${fmtDate(data?.from_date)} to ${fmtDate(data?.to_date)}   |   ` : ''}
              Printed on: {printTime}
            </PDFText>
            <PDFView style={[styles.headerRule, { height: 0.5, marginBottom: 4 }]} />

            {rows.length > 0 ? (
              <PDFView>
                <PDFView style={styles.tableHead}>
                  <TH w={COL.sr}>#</TH>
                  <TH w={COL.type}>Type</TH>
                  <TH w={COL.billNo}>Bill No.</TH>
                  <TH w={COL.date}>Date</TH>
                  <TH w={COL.patient} align="left">Patient Name</TH>
                  <TH w={COL.uhid}>UHID / IPD No.</TH>
                  <TH w={COL.sponsor} align="left">Sponsor</TH>
                  <TH w={COL.disc} align="right">Discount Amount</TH>
                  <TH w={COL.net} align="right">Net Amount</TH>
                </PDFView>
                {rows.map((row, idx) => {
                  const disc = parseFloat(row?.disc_amt) || 0;
                  const net = parseFloat(row?.net_amt) || 0;
                  return (
                    <PDFView key={idx} style={[styles.row, idx % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                      <TD w={COL.sr}>{idx + 1}</TD>
                      <TD w={COL.type}>{row?.type || '-'}</TD>
                      <TD w={COL.billNo}>{row?.bill_no || '-'}</TD>
                      <TD w={COL.date}>{fmtDate(row?.date)}</TD>
                      <TD w={COL.patient} align="left">{row?.patient_name || '-'}</TD>
                      <TD w={COL.uhid}>{(row?.type === 'IPD' ? row?.ipd_no : row?.uhid) || '-'}</TD>
                      <TD w={COL.sponsor} align="left">{row?.sponsor_name || '-'}</TD>
                      <TD w={COL.disc} align="right" color={disc > 0 ? C.red : C.dark}>{fmt(disc)}</TD>
                      <TD w={COL.net} align="right" bold>{fmt(net)}</TD>
                    </PDFView>
                  );
                })}
                <PDFView style={styles.totalRow}>
                  <PDFText style={styles.totalLabel}>Total ({rows.length} bills) — Discount: {fmt(totalDisc)}   Net Amount:</PDFText>
                  <PDFText style={[styles.totalAmt, { width: COL.net }]}>{fmt(totalNet)}</PDFText>
                </PDFView>
              </PDFView>
            ) : (
              <PDFView style={{ borderWidth: 1, borderColor: C.border, marginTop: 10 }}>
                <PDFText style={styles.emptyText}>No records found for the selected doctor and date range.</PDFText>
              </PDFView>
            )}

            <PDFView style={styles.footer} fixed>
              <PDFText style={styles.footerLeft}>Doctor Visits and Bills — {doctorName}  |  Printed: {printTime}</PDFText>
              <PDFText style={styles.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </PDFView>
          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default ViewDoctorVisitsAndBillsPdf;
